import { Alert, AlertColor } from "@mui/material"
import { VideoPlatform } from "app-constants/constants"
import { localTimezone, MomentFormats } from "app-constants/constants"
import { createPath, ROUTE } from "app-constants/routing"
import { ActivityIndicator } from "components/activity-indicator"
import { EmptyState } from "components/empty-state"
import ScheduledMeetingCard from "components/meetings/ScheduledMeetingCard"
import { api } from "lib/api"
import deleteConventionMeetingRegistration from "lib/deleteConventionMeetingRegistration"
import openWindowAsync from "lib/open-window-async"
import useCurrent from "lib/use-current"
import ConventionMeetingRegistration from "models/convention-api/v1/ConventionMeetingRegistration"
import moment from "moment-timezone"
import { useRouter } from "next/router"
import { useSnackbar } from "notistack"
import { useState } from "react"

interface IItineraryMeetings {}

export const ItineraryMeetings = (_props: IItineraryMeetings) => {
  const current = useCurrent()
  const router = useRouter()

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const meetingRegistrations = ConventionMeetingRegistration.useAll(() => {
    if (!current?.user?.id) return null

    return {
      filter: {
        convention_user_id: current.user.id,
      },
      includes: [
        "conventionMeetingAvailability",
        "conventionMeetingAvailability.adminUser",
        "conventionMeetingAvailability.institution",
      ],
    }
  })

  const [deletedMeetingRegistrations, setDeletedMeetingRegistrations] = useState<
    Record<string, true>
  >({})

  const showSnackbar = (message: string, severity: AlertColor = "info") => {
    enqueueSnackbar(message, {
      autoHideDuration: 2000,
      content: (key) => (
        <Alert onClose={() => closeSnackbar(key)} severity={severity}>
          {message}
        </Alert>
      ),
    })
  }

  const onMeetingLaunchPress = async (meetingRegistration: ConventionMeetingRegistration) => {
    const { conventionMeetingAvailability } = meetingRegistration

    if (conventionMeetingAvailability?.video_platform === VideoPlatform.WHEREBY) {
      await router.push(
        createPath({
          path: ROUTE.CONVENTION_MEETING,
          params: { registrationId: meetingRegistration.id },
        }),
      )
      return
    }

    openWindowAsync(async () => {
      await api.joinMeeting({ req: undefined }, meetingRegistration.id)
      return conventionMeetingAvailability?.integrated_meeting_url!
    })
  }

  const onMeetingRemovePress = async (meetingRegistration: ConventionMeetingRegistration) => {
    try {
      if (!current) throw "current is not defined"

      await deleteConventionMeetingRegistration({ current, id: meetingRegistration.id })
      setDeletedMeetingRegistrations((prev) => ({
        ...prev,
        [meetingRegistration.id]: true,
      }))
      showSnackbar("Successfully removed", "success")
    } catch (error) {
      console.error(error)
    } finally {
    }
  }

  if (meetingRegistrations.isValidating && meetingRegistrations.data === undefined) {
    return <ActivityIndicator />
  }

  if (!meetingRegistrations.data || meetingRegistrations.data.length === 0) {
    return <EmptyState title="🎟" subtitle="You aren’t registered for any upcoming meetings" large />
  }

  return (
    <>
      {meetingRegistrations.data.map((meetingRegistration) => {
        const formattedStartTime = moment(meetingRegistration.start_at)
          .tz(localTimezone)
          .format(MomentFormats.endTime)

        const startAt = moment(meetingRegistration.start_at)
        const endAt = moment(meetingRegistration.end_at)

        const institution = meetingRegistration.conventionMeetingAvailability?.institution
        const meetingHost = meetingRegistration.conventionMeetingAvailability?.adminUser

        if (deletedMeetingRegistrations[meetingRegistration.id]) return null

        return (
          <ScheduledMeetingCard
            key={meetingRegistration.id}
            institution={institution}
            startAt={startAt}
            endAt={endAt}
            formattedStartTime={formattedStartTime}
            meetingHost={meetingHost}
            meetingRegistration={meetingRegistration}
            onMeetingLaunchPress={() => onMeetingLaunchPress(meetingRegistration)}
            onMeetingRemovePress={() => onMeetingRemovePress(meetingRegistration)}
          />
        )
      })}
    </>
  )
}
